import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Alert, Box,
  FormControl, InputLabel, Select, MenuItem, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress,
} from '@mui/material';
import { authPost } from '../../services/apiService';

/**
 * Move stored records onto a portal team after an ADO rename.
 *   mode 'merge' → pick another portal team, move its records here, then delete it.
 *   mode 'retag' → move records still stored under this team's ADO / previous names.
 * Always previews counts first; nothing changes until the admin confirms.
 */
const TeamRecordsDialog = ({ open, mode, team, teams, onClose, onDone }) => {
  const [sourceId, setSourceId] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isMerge = mode === 'merge';
  const url = team ? `/api/admin/teams/${encodeURIComponent(team.id)}/${isMerge ? 'merge' : 'retag'}` : '';
  const others = (teams || []).filter((t) => t.id !== team?.id);

  useEffect(() => {
    if (!open) return;
    setSourceId('');
    setPreview(null);
    setError(null);
  }, [open, mode, team]);

  const run = async (dryRun) => {
    setBusy(true);
    setError(null);
    try {
      const res = await authPost(url, { dryRun, ...(isMerge && { sourceId }) });
      if (!res.success) throw new Error(res.message || 'Request failed');
      if (dryRun) setPreview(res.data);
      else onDone?.(res.data);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  // Retag has no input, so preview straight away.
  useEffect(() => {
    if (open && !isMerge && team) run(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMerge, team]);

  const source = others.find((t) => t.id === sourceId);
  const totals = preview?.totals;
  const nothingToMove = totals && !totals.records && !totals.nested;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="team-records-title">
      <DialogTitle id="team-records-title">
        {isMerge ? `Merge a team into ${team?.displayName || team?.id}` : `Move records from old names to ${team?.displayName || team?.id}`}
      </DialogTitle>
      <DialogContent>
        {isMerge ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use this when a rename in ADO created a second portal team. Its records move to
              {' '}<strong>{team?.id}</strong>, its names are kept as previous ADO names, and the other team is deleted.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="merge-source-label">Team to merge in</InputLabel>
              <Select
                labelId="merge-source-label"
                label="Team to merge in"
                value={sourceId}
                onChange={(e) => { setSourceId(e.target.value); setPreview(null); }}
              >
                {others.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.displayName} ({t.id})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Records synced before the names were set up may still carry an old team name. This moves them onto
            {' '}<strong>{team?.id}</strong> so reports show the full history.
          </Typography>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {busy && !preview && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress size={28} /></Box>}

        {preview && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Names: {preview.names?.length ? preview.names.join(', ') : '—'}
            </Typography>
            {nothingToMove && !totals.conflicts ? (
              <Alert severity="info">No records are stored under {preview.names?.length > 1 ? 'these names' : 'this name'}.</Alert>
            ) : (
              <Table size="small" aria-label="Records to move">
                <TableHead>
                  <TableRow>
                    <TableCell>Collection</TableCell>
                    <TableCell align="right">Records</TableCell>
                    <TableCell align="right">With nested items</TableCell>
                    <TableCell align="right">Kept (already exist)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.collections.map((c) => (
                    <TableRow key={c.model}>
                      <TableCell>{c.model}</TableCell>
                      <TableCell align="right">{c.records}</TableCell>
                      <TableCell align="right">{c.nested}</TableCell>
                      <TableCell align="right">{c.conflicts || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {totals?.conflicts > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {totals.conflicts} record{totals.conflicts === 1 ? '' : 's'} already exist for {team?.id} for the same sprint or quarter
                {' '}and will be left under the old name.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {isMerge && !preview && (
          <Button variant="outlined" onClick={() => run(true)} disabled={!sourceId || busy}>Preview</Button>
        )}
        {preview && (isMerge || !nothingToMove) && (
          <Button variant="contained" color={isMerge ? 'error' : 'primary'} onClick={() => run(false)} disabled={busy}>
            {busy ? 'Working…' : isMerge ? `Merge and delete ${source?.id || ''}` : 'Move records'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TeamRecordsDialog;
